import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, Search } from "lucide-react";
import type { ProductSummaryDto } from "@hisense/shared";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { useProducts } from "@/api/hooks";

/**
 * Search the catalog and pick one product. Used by the sample modules to link a
 * line (or a card) to a real product instead of retyping its identity.
 */
export function ProductPickerModal({
  onPick,
  onClose,
  takenProductIds = [],
}: {
  onPick: (product: ProductSummaryDto) => void;
  onClose: () => void;
  /** Already on the document — shown, but not selectable twice. */
  takenProductIds?: string[];
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const deferredQ = useDeferredValue(q.trim());
  const { data, isLoading, error, refetch } = useProducts({
    q: deferredQ || undefined,
    page: 1,
    pageSize: 25,
  });
  const taken = new Set(takenProductIds);
  const rows = data?.data ?? [];

  return (
    <Modal title={t("samples.addProduct")} closeLabel={t("common.close")} onClose={onClose} className="max-w-2xl">
      <div className="p-4">
        <Input
          data-autofocus="true"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          leading={<Search />}
          placeholder={t("samples.searchProducts")}
        />
        <div className="mt-3 max-h-[26rem] overflow-y-auto rounded-sm border border-line">
          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={t("products.errorLoad")} onRetry={() => refetch()} />
          ) : rows.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-ink-faint">{t("common.noMatches")}</p>
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((p) => {
                const already = taken.has(p.id);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      disabled={already}
                      title={already ? t("samples.duplicateProduct") : undefined}
                      onClick={() => onPick(p)}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-sunken disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt="" className="size-9 shrink-0 rounded-sm border border-line object-cover" />
                      ) : (
                        <span className="grid size-9 shrink-0 place-items-center rounded-sm border border-line bg-sunken/40">
                          <ImageIcon className="size-4 text-ink-faint" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-mono tabular text-xs font-medium text-brand">{p.productCode}{p.subRef ? ` / ${p.subRef}` : ""}</span>
                        <span className="block truncate text-xs text-ink-soft">{p.description ?? "—"}</span>
                      </span>
                      <span className="hidden shrink-0 text-2xs text-ink-faint sm:block">
                        {[p.type, p.brandName].filter(Boolean).join(" · ")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
